import React, { useEffect, useRef, useState } from 'react';
import { playWord } from './_audio';

const HT = {
  yellow: '#FFD166', yellowSoft: '#FFE9A8', yellowBg: '#FFF8E7', yellowShadow: '#E0B554',
  green: '#88C057', greenDark: '#5E9E3E', greenSoft: '#D4E8C0', greenBg: '#E8F3D9',
  coral: '#FF8C42', coralDark: '#E76F51', coralPress: '#B8431E',
  pinkBg: '#FCE4E8', pink: '#F4A6B8',
  brown: '#8B6F47', brownDark: '#6B4F2B',
  text: '#2D2D2D', textSoft: '#6B6B6B', textMuted: '#9A9A9A',
  border: '#E8E0CC', white: '#FFFFFF', teal: '#5AC0B5',
  space: '#0a0a23', spaceDeep: '#040414', neon: '#7df9ff',
};
const FONT_DISPLAY = '"Fredoka", "Baloo 2", "Nunito", sans-serif';
const FONT_BODY = '"Nunito", "Baloo 2", sans-serif';

const ANIM_CSS = `
@keyframes sdFall {
  from { top: -14%; }
  to { top: 78%; }
}
@keyframes sdSpin { from { transform:rotate(0) } to { transform:rotate(360deg) } }
@keyframes sdStar { 0%,100%{opacity:.3} 50%{opacity:1} }
@keyframes sdLaser {
  0% { transform: scaleY(0); transform-origin: bottom; opacity: 0; }
  20% { transform: scaleY(1); transform-origin: bottom; opacity: 1; }
  100% { transform: scaleY(1); transform-origin: bottom; opacity: 0; }
}
@keyframes sdExplode {
  0% { transform: scale(1); opacity: 1; }
  60% { transform: scale(2); opacity: 0.7; }
  100% { transform: scale(3); opacity: 0; }
}
@keyframes sdCoin {
  0% { transform: translate(0,0) rotate(0); opacity: 1; }
  100% { transform: translate(var(--cx),120px) rotate(var(--cr)); opacity: 0; }
}
@keyframes sdShake { 0%,100%{transform:translate(0,0)} 25%{transform:translate(-6px,2px)} 75%{transform:translate(6px,-2px)} }
@keyframes sdAlarm { 0%,100%{opacity:1} 50%{opacity:0.3} }
@keyframes sdCharge {
  from { width: 0%; }
  to { width: 100%; }
}
.sd-shake { animation: sdShake 0.18s linear 4; }
`;

function injectCss() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('defender-anim-css')) return;
  const el = document.createElement('style');
  el.id = 'defender-anim-css';
  el.textContent = ANIM_CSS;
  document.head.appendChild(el);
}

function levenshtein(a, b) {
  const m = a.length, n = b.length;
  if (!m) return n;
  if (!n) return m;
  const d = Array.from({ length: m + 1 }, () => new Array(n + 1));
  for (let i = 0; i <= m; i++) d[i][0] = i;
  for (let j = 0; j <= n; j++) d[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
    }
  }
  return d[m][n];
}

export default function SpaceDefenderGame({ payload, onAnswer, disabled }) {
  const isImageMode = payload.cue_kind === 'image_sound';
  const isDefMode = payload.cue_kind === 'definition';
  const fallMs = payload.fall_duration_ms || 14000;
  const maxLev = payload.max_levenshtein ?? 2;
  const answer = (payload.answer || '').trim();
  const answerLower = (payload.answer_lower || answer.toLowerCase());

  const [input, setInput] = useState('');
  const [phase, setPhase] = useState('falling'); // 'falling' | 'exploded' | 'crashed'
  const [wrongShake, setWrongShake] = useState(0);
  const [wrongAttempts, setWrongAttempts] = useState(0);
  const [givenUp, setGivenUp] = useState(false);
  const fallTimerRef = useRef(null);
  const inputRef = useRef(null);
  const meteorRef = useRef(null);

  useEffect(() => { injectCss(); }, []);

  // Auto-focus input + auto-play sound (image mode)
  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  useEffect(() => {
    if (!isImageMode) return;
    let cleanup = null;
    const t = setTimeout(() => {
      cleanup = playWord(payload.audio_url, payload.tts_text || payload.answer);
    }, 350);
    return () => {
      clearTimeout(t);
      if (cleanup) cleanup();
    };
  }, [isImageMode, payload.audio_url, payload.tts_text, payload.answer]);

  // Schedule meteor-hit timeout
  useEffect(() => {
    fallTimerRef.current = setTimeout(() => {
      // Only crash if still falling
      setPhase(p => p === 'falling' ? 'crashed' : p);
    }, fallMs);
    return () => clearTimeout(fallTimerRef.current);
  }, [fallMs]);

  // Phase transitions → onAnswer
  useEffect(() => {
    if (phase === 'exploded') {
      const t = setTimeout(() => {
        if (onAnswer) onAnswer({
          is_correct: true,
          user_answer: input.toLowerCase(),
        });
      }, 1000);
      return () => clearTimeout(t);
    }
    if (phase === 'crashed' && !givenUp) {
      const t = setTimeout(() => {
        if (onAnswer) onAnswer({
          is_correct: false,
          user_answer: input.toLowerCase(),
        });
      }, 1300);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line
  }, [phase]);

  const submit = () => {
    if (phase !== 'falling' || disabled) return;
    const guess = input.trim().toLowerCase();
    if (!guess) return;
    const dist = levenshtein(guess, answerLower);
    if (dist <= maxLev) {
      // Cancel fall timer immediately, switch to exploded
      clearTimeout(fallTimerRef.current);
      setPhase('exploded');
    } else {
      setWrongAttempts(w => w + 1);
      setWrongShake(s => s + 1);
      setInput('');
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') submit();
  };

  const giveUp = () => {
    if (phase !== 'falling') return;
    clearTimeout(fallTimerRef.current);
    setGivenUp(true);
    setPhase('crashed');
    setTimeout(() => {
      if (onAnswer) onAnswer({
        is_correct: false,
        user_answer: input.toLowerCase(),
      });
    }, 800);
  };

  // Compute charge percentage: matched prefix length / answer length
  const chargePct = (() => {
    const g = input.toLowerCase();
    let i = 0;
    while (i < g.length && i < answerLower.length && g[i] === answerLower[i]) i++;
    return Math.min(100, Math.round((i / Math.max(answerLower.length, 1)) * 100));
  })();

  return (
    <div style={{
      width: 'calc(100vw - 32px)',
      marginLeft: 'calc(-50vw + 50% + 16px)',
      marginRight: 'calc(-50vw + 50% + 16px)',
      fontFamily: FONT_BODY,
      padding: 0,
      height: 'calc(100vh - 120px)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 12,
    }}>
      <div
        className={wrongShake ? 'sd-shake' : ''}
        key={wrongShake}
        style={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          width: '100%',
          maxWidth: 1400,
          borderRadius: 28,
          background: `radial-gradient(ellipse at 50% 20%, #1e3a5f 0%, ${HT.space} 60%, ${HT.spaceDeep} 100%)`,
          border: `2px solid ${HT.yellow}`,
          overflow: 'hidden',
          boxShadow: '0 4px 16px rgba(139,111,71,0.10)',
        }}
      >
        {/* Stars */}
        {Array.from({ length: 36 }).map((_, i) => (
          <div key={i} style={{
            position: 'absolute',
            top: `${(i * 7.3) % 70}%`,
            left: `${(i * 11.7) % 100}%`,
            width: i % 5 === 0 ? 3 : 2, height: i % 5 === 0 ? 3 : 2,
            background: i % 7 === 0 ? HT.neon : HT.white,
            borderRadius: '50%',
            opacity: 0.5 + (i % 3) * 0.15,
            animation: `sdStar ${2 + (i % 4)}s ease-in-out infinite`,
            animationDelay: `${(i % 5) * 0.4}s`,
          }} />
        ))}

        {/* HUD top intentionally empty in def mode — the cue (meaning + image)
            lives just above the input panel so it stays on screen while the
            phone keyboard is open. */}

        {/* Meteor */}
        {phase === 'falling' && (
          <div
            ref={meteorRef}
            style={{
              position: 'absolute',
              left: '50%', top: '-14%',
              transform: 'translateX(-50%)',
              animation: `sdFall ${fallMs}ms linear forwards`,
              zIndex: 3,
            }}
          >
            <Meteor isImageMode={isImageMode} imageUrl={payload.image_url} />
          </div>
        )}

        {/* Explosion at meteor's position */}
        {phase === 'exploded' && (
          <>
            <div style={{
              position: 'absolute', left: '50%', bottom: 190,
              transform: 'translateX(-50%)',
              fontSize: 100,
              animation: 'sdExplode 1s ease-out forwards',
              zIndex: 4,
            }}>💥</div>
            {/* Coin shower */}
            {Array.from({ length: 12 }).map((_, i) => {
              const cx = (Math.random() - 0.5) * 240;
              const cr = (Math.random() - 0.5) * 720;
              return (
                <div key={i} style={{
                  position: 'absolute',
                  left: '50%', bottom: 210,
                  fontSize: 26,
                  animation: 'sdCoin 1.2s ease-out forwards',
                  animationDelay: `${i * 0.04}s`,
                  '--cx': `${cx}px`, '--cr': `${cr}deg`,
                  zIndex: 4,
                }}>🪙</div>
              );
            })}
            {/* Laser */}
            <div style={{
              position: 'absolute',
              left: '50%', bottom: 110,
              width: 10, height: 380,
              transform: 'translateX(-50%)',
              background: `linear-gradient(180deg, ${HT.neon} 0%, ${HT.coral} 100%)`,
              boxShadow: `0 0 20px ${HT.neon}, 0 0 40px ${HT.neon}`,
              animation: 'sdLaser 0.6s ease-out forwards',
              zIndex: 3, borderRadius: 4,
            }} />
          </>
        )}

        {/* Crashed (failed) */}
        {phase === 'crashed' && (
          <>
            <div style={{
              position: 'absolute', left: '50%', bottom: 150,
              transform: 'translateX(-50%)',
              fontSize: 90,
              animation: 'sdAlarm 0.4s linear infinite',
              zIndex: 4,
            }}>💥</div>
            <div style={{
              position: 'absolute', top: 12, left: 12, right: 12,
              padding: '8px 14px',
              background: '#7f1d1d', color: '#fef2f2',
              borderRadius: 8, textAlign: 'center',
              fontSize: 13, fontWeight: 800, letterSpacing: 1.5,
              animation: 'sdAlarm 0.5s linear infinite',
              zIndex: 5,
            }}>🚨 SHIELD NỨT — ĐÁP ÁN: {answer.toUpperCase()}</div>
          </>
        )}

        {/* City + Shield + Turret */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          height: 150, zIndex: 2,
        }}>
          {/* Shield arc */}
          <div style={{
            position: 'absolute', bottom: 100, left: '50%',
            transform: 'translateX(-50%)',
            width: '85%', height: 46,
            border: `3px solid ${phase === 'crashed' ? '#dc2626' : HT.neon}`,
            borderBottom: 'none',
            borderRadius: '100% 100% 0 0',
            opacity: phase === 'crashed' ? 0.4 : 0.7,
            boxShadow: phase === 'crashed' ? 'none' : `0 0 16px ${HT.neon}`,
          }} />
          {/* City skyline */}
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            height: 100, display: 'flex', alignItems: 'flex-end',
            justifyContent: 'space-around',
          }}>
            {[64, 96, 48, 112, 80, 72, 104, 56, 88, 60, 100, 52, 92, 68].map((h, i) => (
              <div key={i} style={{
                width: 44, height: h,
                background: '#1a1a3e',
                border: `1px solid ${HT.neon}`,
                borderBottom: 'none',
                opacity: 0.8,
              }} />
            ))}
          </div>
          {/* Turret */}
          <div style={{
            position: 'absolute', bottom: 0, left: '50%',
            transform: 'translateX(-50%)',
            width: 90, height: 74,
          }}>
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              height: 44, background: HT.brownDark, borderRadius: '10px 10px 0 0',
              border: `3px solid ${HT.yellow}`,
            }} />
            <div style={{
              position: 'absolute', bottom: 40, left: '50%',
              transform: 'translateX(-50%)',
              width: 20, height: 38,
              background: HT.yellow,
              border: `3px solid ${HT.brownDark}`,
              borderRadius: '6px 6px 0 0',
            }} />
          </div>
        </div>
      </div>

      {/* Cue row — meaning + picture, glued to the input panel so it stays
          visible while the phone keyboard is open. name_vi/image_url may be
          absent in older payloads: recover meaning from explanation_vi
          ("'word' = 'nghĩa'."), fall back to the definition text. */}
      {(() => {
        const vi = payload.name_vi
          || (payload.explanation_vi || '').match(/=\s*'([^']+)'/)?.[1]
          || '';
        const img = payload.image_url;
        if (!vi && !img && !isDefMode) return null;
        return (
          <div style={{
            flexShrink: 0, width: '100%', maxWidth: 760, margin: '0 auto',
            padding: '8px 12px',
            background: HT.space,
            border: `1px solid ${HT.neon}`, borderRadius: 14,
            display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center',
          }}>
            {img && (
              <img
                src={img}
                alt=""
                style={{
                  width: 56, height: 56, borderRadius: 12, objectFit: 'cover',
                  background: HT.white, border: `2px solid ${HT.yellow}`, flexShrink: 0,
                }}
              />
            )}
            <div style={{ minWidth: 0, textAlign: img ? 'left' : 'center' }}>
              <div style={{ fontSize: 10, color: HT.neon, letterSpacing: 2, fontFamily: 'monospace', opacity: 0.8 }}>
                📡 BÁO CÁO TỪ HQ
              </div>
              <div style={{
                fontSize: 16, fontWeight: 800, color: HT.white,
                fontFamily: FONT_DISPLAY, lineHeight: 1.2,
              }}>
                {vi || payload.definition_vi || payload.definition_en || '—'}
              </div>
              {payload.first_letter_hint && wrongAttempts >= 1 && (
                <div style={{ marginTop: 2, color: HT.yellow, fontSize: 11, fontWeight: 700 }}>
                  💡 Bắt đầu bằng "<b>{payload.first_letter_hint}</b>", có {payload.length_hint} chữ
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Input panel */}
      <div style={{
        flexShrink: 0,
        width: '100%', maxWidth: 760, margin: '0 auto',
        padding: '14px 16px',
        background: HT.space, color: HT.white,
        borderRadius: 18, border: `2px solid ${HT.neon}`,
      }}>
        {/* Charge bar */}
        <div style={{
          height: 8, background: '#1a1a3e', borderRadius: 999,
          overflow: 'hidden', marginBottom: 10,
        }}>
          <div style={{
            height: '100%',
            width: `${chargePct}%`,
            background: chargePct === 100
              ? `linear-gradient(90deg, ${HT.green}, ${HT.neon})`
              : `linear-gradient(90deg, ${HT.coral}, ${HT.yellow})`,
            transition: 'width 0.2s',
            boxShadow: `0 0 12px ${chargePct === 100 ? HT.green : HT.coral}`,
          }} />
        </div>

        <div style={{
          fontSize: 11, color: HT.neon, letterSpacing: 1.5, marginBottom: 6,
          fontFamily: 'monospace',
        }}>
          MẬT MÃ PHÁ HỦY ({payload.length_hint || answer.length} ký tự):
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={phase !== 'falling' || disabled}
            placeholder="gõ mật mã..."
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            style={{
              flex: 1, padding: '12px 16px',
              background: '#1a1a3e', color: HT.white,
              border: `2px solid ${phase === 'falling' ? HT.neon : HT.textMuted}`,
              borderRadius: 12, fontSize: 18, fontWeight: 700,
              fontFamily: 'monospace', letterSpacing: 2,
              outline: 'none',
              textTransform: 'lowercase',
            }}
          />
          <button
            onClick={submit}
            disabled={phase !== 'falling' || !input.trim() || disabled}
            style={{
              padding: '12px 22px', borderRadius: 12, border: 'none',
              background: input.trim() && phase === 'falling' ? HT.coralDark : '#3a3a5e',
              color: HT.white, fontWeight: 800, fontSize: 14,
              fontFamily: FONT_DISPLAY, cursor: 'pointer',
              boxShadow: input.trim() && phase === 'falling' ? `0 4px 0 ${HT.coralPress}` : 'none',
            }}
          >🔥 FIRE</button>
        </div>

        <div style={{
          marginTop: 8, display: 'flex', gap: 12,
          fontSize: 11, color: HT.textMuted,
        }}>
          <span>Sai: {wrongAttempts}</span>
          <span>•</span>
          <span>Sai cho phép: ≤ {maxLev} ký tự</span>
          {wrongAttempts >= 2 && phase === 'falling' && (
            <button onClick={giveUp} style={{
              marginLeft: 'auto', padding: '2px 10px', borderRadius: 999,
              border: 'none', background: HT.textMuted, color: HT.white,
              fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}>Bỏ cuộc</button>
          )}
        </div>
      </div>

      {/* Result footer */}
      {phase === 'exploded' && (
        <div style={{
          flexShrink: 0, width: '100%', maxWidth: 760, margin: '0 auto',
          padding: '12px 18px',
          background: HT.greenBg, border: `2px solid ${HT.greenDark}`,
          borderRadius: 14, textAlign: 'center',
          fontSize: 14, fontWeight: 700, color: HT.text,
        }}>
          🚀 Phá hủy thành công! Đáp án: <b style={{ color: HT.greenDark }}>{answer}</b>
        </div>
      )}
      {phase === 'crashed' && (
        <div style={{
          flexShrink: 0, width: '100%', maxWidth: 760, margin: '0 auto',
          padding: '12px 18px',
          background: HT.pinkBg, border: `2px solid ${HT.coral}`,
          borderRadius: 14, textAlign: 'center',
          fontSize: 14, fontWeight: 700, color: HT.text,
        }}>
          🛡️ Shield nứt! Đáp án đúng là <b style={{ color: HT.coralDark }}>{answer}</b>.
        </div>
      )}
    </div>
  );
}

function Meteor({ isImageMode, imageUrl }) {
  return (
    <div style={{ position: 'relative', width: 150, height: 150 }}>
      {/* Trail */}
      <div style={{
        position: 'absolute', top: -42, left: '50%',
        transform: 'translateX(-50%)',
        width: 34, height: 84,
        background: `linear-gradient(180deg, transparent, ${HT.coralDark}, ${HT.yellow})`,
        borderRadius: '50% 50% 4px 4px',
        opacity: 0.7,
        filter: 'blur(3px)',
      }} />
      {/* Rock body */}
      <div style={{
        position: 'absolute', top: 0, left: 0,
        width: 150, height: 150,
        background: `radial-gradient(circle at 35% 30%, #b89070 0%, ${HT.brownDark} 60%, #2a1610 100%)`,
        border: `3px solid ${HT.coralDark}`,
        borderRadius: '50%',
        boxShadow: `0 0 30px ${HT.coral}, inset -10px -10px 24px rgba(0,0,0,0.4)`,
        animation: 'sdSpin 3s linear infinite',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {isImageMode && imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            style={{
              width: 100, height: 100, objectFit: 'contain',
              background: HT.white, borderRadius: '50%',
              padding: 6,
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
            }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <span style={{ fontSize: 76 }}>☄️</span>
        )}
      </div>
      {/* Sparks */}
      <div style={{ position: 'absolute', top: -14, left: -12, fontSize: 24 }}>✨</div>
      <div style={{ position: 'absolute', top: -10, right: -6, fontSize: 20 }}>⭐</div>
    </div>
  );
}
